import { useState } from "react";
import { DoctorService } from "../../../services/api/doctor.service";
import toast from "react-hot-toast";

interface ClinicFormProps {
  formData: any;
  onChange: (field: string, value: string) => void;
  isGenerating: boolean;
}
const ClinicForm = ({ formData, onChange, isGenerating }: ClinicFormProps) => {
  const handleInputChange = (field: string, value: string) => {
    if (onChange) {
      onChange(field, value);
    }
  };

  const [googleMapLink, setGoogleMapLink] = useState("");
  const [fetchingLocation, setFetchingLocation] = useState(false);

  const handleFetchLocation = async () => {
    if (!googleMapLink) {
      toast.error("Please enter a Google Maps link");
      return;
    }

    setFetchingLocation(true);
    try {
      const response = await DoctorService.getMapDetails(googleMapLink);

      if (response.success && response.data) {
        const { lat, long, address, city, state, pincode } = response.data;
        onChange("address", address || formData.address);
        onChange("city", city || formData.city);
        onChange("state", state || formData.state);
        onChange("pincode", pincode || formData.pincode);
        onChange("lat", lat.toString());
        onChange("long", long.toString());

        toast.success("Location details fetched successfully!");
      } else {
        toast.error(response.message || "Failed to fetch location details");
      }
    } catch (error) {
      console.error("Error fetching location:", error);
      toast.error("An error occurred while fetching location details");
    } finally {
      setFetchingLocation(false);
    }
  };

  return (
    <div className="w-full bg-white border-b border-gray-500/30">
      {/* Clinic Name */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Clinic Name
        </label>
        <input
          type="text"
          disabled={isGenerating}
          placeholder="Enter clinic name"
          value={formData.name || ""}
          onChange={(e) => handleInputChange("name", e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
        />
      </div>

      <div className="mb-4">
        <label className="block text-sm text-gray-700 font-medium mb-2">
          Google Map Link
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            disabled={isGenerating}
            value={googleMapLink}
            onChange={(e) => setGoogleMapLink(e.target.value)}
            className="flex-1 px-3 py-2 border border-gray-300 outline-none rounded-lg focus:ring-1 focus:ring-green-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            placeholder="Paste Google Maps Link"
          />
          <button
            type="button"
            onClick={handleFetchLocation}
            disabled={fetchingLocation || isGenerating}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {fetchingLocation ? "Fetching..." : "Fetch Location"}
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          Paste a Google Maps link and click "Fetch Location" to auto-fill
          details.
        </p>
      </div>

      {/* Address and City Row */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Address
          </label>
          <input
            type="text"
            disabled={isGenerating}
            placeholder="Enter clinic Address"
            value={formData.address || ""}
            onChange={(e) => handleInputChange("address", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            City
          </label>
          <input
            type="text"
            disabled={isGenerating}
            placeholder="Enter clinic city"
            value={formData.city || ""}
            onChange={(e) => handleInputChange("city", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4 mb-4">
        {/* State */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            State
          </label>
          <input
            type="text"
            disabled={isGenerating}
            placeholder="Enter clinic state"
            value={formData.state || ""}
            onChange={(e) => handleInputChange("state", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
        </div>
        {/* pincode */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Pincode
          </label>
          <input
            type="text"
            disabled={isGenerating}
            placeholder="Enter clinic pincode"
            maxLength={6}
            value={formData.pincode || ""}
            onChange={(e) => handleInputChange("pincode", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Mobile Number */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Mobile Number
        </label>
        <input
          type="tel"
          disabled={isGenerating}
          maxLength={10}
          placeholder="Enter clinic mobile number"
          value={formData.mobile_number || ""}
          onChange={(e) => handleInputChange("mobile_number", e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
        />
      </div>
    </div>
  );
};

export default ClinicForm;
